import React, { useEffect, useState } from 'react';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Card,
  CardContent,
  Box,
  Button,
  Tooltip,
  Avatar,
  Typography,
  TableContainer,
  InputAdornment,
  TextField,
  CircularProgress,
  Badge,
  Chip,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InventoryIcon from '@mui/icons-material/Inventory';
import QrCodeIcon from '@mui/icons-material/QrCode';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { request } from '../../../../utils/request';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDebounce } from '../../../../utils/useDebounce';
import Brandcrumb from '../../../../componentsUiBlock/Brandcrumb';
import PriceCodeDisplay from '../../../controlPanel/components/PriceCodeDisplay.tsx';
import DeleteConfirmDialog from '../../../../componentsUiBlock/DeleteConfirmDialog';
import { useDeleteDialog } from '../../../../utils/useDeleteDialog';
import { HeadCell, Product } from '../../types';
import { usePermissions } from '../../../../utils/usePermissions';
import { baseUrlImage } from '../../../../utils/apiClient';

const headCells: HeadCell[] = [
  { id: 'partNo', label: 'Product' },
  { id: 'name', label: 'Name' },
  { id: 'quantity', label: 'Qnt.' },
  { id: 'brand', label: 'Brand' },
  { id: 'category', label: 'Category' },
  { id: 'vender', label: 'Vendor' },
  { id: 'purchasePrice', label: 'Pricing' },
  { id: 'compatibility', label: 'Compatibility' },
  { id: 'action', label: 'Actions' }
];

export const DEMO_IMAGE_URL = 'https://static.vecteezy.com/system/resources/thumbnails/049/675/565/small/a-black-and-white-gear-wheel-icon-png.png';

const ProductsList: React.FC = () => {
  const { canCreateInventory, canDeleteInventory } = usePermissions();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [orderBy, setOrderBy] = useState<keyof Product>('name');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const debouncedSearch = useDebounce(search, 500);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  // const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const url = debouncedSearch ? `product/get-all-products?search=${encodeURIComponent(debouncedSearch)}` : `product/get-all-products`;
        const res = await request({
          url: url,
          method: "get",
        });
        if (res.status === "success") {
          setProducts(res.products || []);
          setTotal(res.total || 0);
        }
      } catch (error: any) {
        console.error('Error fetching products:', error);
        if (error?.response?.status !== 404) {
          toast.error(error || 'Failed to load products');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [debouncedSearch]);

  const handleSort = (property: HeadCell['id']) => {
    if (property === 'action') return;

    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property as keyof Product);
  };

  const sortedProducts = [...products].sort((a, b) => {
    const aValue = a[orderBy];
    const bValue = b[orderBy];

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return order === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return order === 'asc' ? aValue - bValue : bValue - aValue;
    }

    if (Array.isArray(aValue) && Array.isArray(bValue)) {
      const aString = aValue.join(', ');
      const bString = bValue.join(', ');
      return order === 'asc' ? aString.localeCompare(bString) : bString.localeCompare(aString);
    }

    return 0;
  });

  const deleteProduct = async (product: Product) => {
    try {
      const res = await request({
        url: `product/delete-product/${product._id}`,
        method: 'delete',
      });

      if (res?.status === 'success') {
        toast.success(res?.message || 'Product deleted successfully');
        setProducts(prev => prev.filter(p => p._id !== product._id));
      } else {
        throw new Error(res?.message || 'Failed to delete product');
      }
    } catch (error: any) {
      toast.error(error)
    }
  };

  const { open, loadingDelete, itemToDelete, openDialog, closeDialog, confirmDelete } = useDeleteDialog<Product>({
    onDelete: deleteProduct,
  })

  const getStatusColor = (status: string) => {
    return status === 'active' ? '#00C389' : '#FF4D6D';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const renderCompatibilityDetails = (compatibility: Product['compatibility']) => {
    if (!compatibility || compatibility.length === 0) {
      return (
        <Typography variant="caption" color="text.secondary" fontStyle="italic">
          No compatibility
        </Typography>
      );
    }

    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {compatibility.slice(0, 1).map((comp, index) => (
          <Box key={index} sx={{
            display: "flex",
            flexDirection: "column",
            p: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            backgroundColor: 'background.default'
          }}>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ mb: 0.5 }}
            >
              {comp.brand.name} {comp.name}
            </Typography>

            {comp.variants && comp.variants.length > 0 && (
              <Box sx={{}}>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                  Variants:
                </Typography>
                <Typography variant="caption" component="span">
                  {comp.variants.slice(0, 3).join(', ')}
                  {comp.variants.length > 3 && ` +${comp.variants.length - 3} more`}
                </Typography>
              </Box>
            )}

            {comp.fuelTypes && comp.fuelTypes.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                  Fuel:
                </Typography>
                <Typography variant="caption" component="span">
                  {comp.fuelTypes.join(', ')}
                </Typography>
              </Box>
            )}

            {comp.transmissions && comp.transmissions.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                  Trans:
                </Typography>
                <Typography variant="caption" component="span">
                  {comp.transmissions.join(', ')}
                </Typography>
              </Box>
            )}

            {comp.generations && comp.generations.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                  Gen:
                </Typography>
                <Typography variant="caption" component="span">
                  {comp.generations[0].from}-{comp.generations[0].to}
                  {comp.generations.length > 1 && ` (+${comp.generations.length - 1})`}
                </Typography>
              </Box>
            )}
          </Box>
        ))}

        {compatibility.length > 2 && (
          <Chip
            label={`+${compatibility.length - 2} more vehicles`}
            size="small"
            color="default"
            variant="filled"
            sx={{ fontSize: '0.7rem', alignSelf: 'flex-start' }}
          />
        )}
      </Box>
    );
  };

  // Responsive table headers configuration
  const getVisibleHeadCells = () => {
    if (isMobile) {
      return headCells.filter(cell => ['partNo', 'quantity', 'action'].includes(cell.id));
    } else if (isTablet) {
      return headCells.filter(cell => !['brand', 'vender', 'compatibility'].includes(cell.id));
    }
    return headCells;
  };

  const renderTableCell = (product: Product, cellId: string) => {
    switch (cellId) {
      case 'partNo':
        return (
          <TableCell>
            <Box display="flex" alignItems="flex-start" gap={.5} flexDirection={isMobile ? "column" : "row"}>
              <Avatar
                variant="rounded"
                // alt={product.name}
                // src={(baseUrlImage + product.productImages?.[0]) || product.productImages?.[0] || DEMO_IMAGE_URL}
                sx={{
                  width: isMobile ? 40 : 60,
                  height: isMobile ? 40 : 60,
                  bgcolor: 'grey.100',
                  '& img': {
                    objectFit: 'contain',
                  }
                }}
              >
                <Box
                  component="img"
                  // src={(baseUrlImage + product.productImages?.[0]) || product.productImages?.[0] || DEMO_IMAGE_URL}
                  src={product.productImages?.[0] ? (baseUrlImage + product.productImages?.[0]) : DEMO_IMAGE_URL}
                  alt={product.name}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    p: 0.5,
                  }}
                />
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color="primary"
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontSize: isMobile ? '0.75rem' : '0.875rem'
                    }}
                  >
                    {product.partNo}
                  </Typography>
                  <Tooltip title={product.status === 'active' ? 'Active' : 'Inactive'}>
                    <FiberManualRecordIcon
                      sx={{
                        fontSize: isMobile ? 8 : 12,
                        color: getStatusColor(product.status)
                      }}
                    />
                  </Tooltip>
                </Box>
                {product.barcode && !isMobile && (
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <QrCodeIcon sx={{ fontSize: isMobile ? 10 : 14, color: 'text.secondary' }} />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}
                    >
                      {product.barcode}
                    </Typography>
                  </Box>
                )}
                {isMobile && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {product.name}
                  </Typography>
                )}
              </Box>
            </Box>
          </TableCell>
        );

      case 'name':
        if (isMobile) return null;
        return (
          <TableCell>
            <Tooltip title={product.name} arrow>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  mb: 0.5,
                  fontSize: isTablet ? '0.75rem' : '0.875rem'
                }}
              >
                {product.name}
              </Typography>
            </Tooltip>
          </TableCell>
        );

      case 'quantity':
        return (
          <TableCell>
            <Box display="flex" alignItems="center" gap={1} flexDirection={isMobile ? "column" : "row"}>
              <Typography
                variant="body2"
                fontWeight={600}
                color={product.quantity === 0 ? 'error.main' : product.quantity < 10 ? 'warning.main' : 'success.main'}
                sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
              >
                {product.quantity}
              </Typography>
              {product.quantity === 0 && !isMobile && (
                <Chip
                  label="Out of Stock"
                  size="small"
                  color="error"
                  variant="filled"
                />
              )}
              {isMobile && product.quantity === 0 && (
                <Chip
                  label="OOS"
                  size="small"
                  color="error"
                  variant="filled"
                  sx={{ fontSize: '0.6rem' }}
                />
              )}
            </Box>
          </TableCell>
        );

      case 'brand':
        if (isMobile || isTablet) return null;
        return (
          <TableCell>
            <Typography variant="body2" sx={{ fontSize: isTablet ? '0.75rem' : '0.875rem' }}>
              {product.brand}
            </Typography>
          </TableCell>
        );

      case 'category':
        if (isMobile) return null;
        return (
          <TableCell>
            <Tooltip title={product.category} arrow placement='top'>
              <Chip
                label={product.category}
                size="small"
                variant="filled"
                sx={{
                  fontSize: isTablet ? '0.6rem' : '0.75rem',
                  borderRadius: 5,
                  px: .8,
                  maxWidth: '100px',
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
              />
            </Tooltip>
          </TableCell>
        );

      case 'vender':
        if (isMobile || isTablet) return null;
        return (
          <TableCell>
            <Tooltip title={product.vender} arrow placement='top'>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '150px',
                  fontSize: isTablet ? '0.75rem' : '0.875rem'
                }}
              >
                {product.vender}
              </Typography>
            </Tooltip>
          </TableCell>
        );

      case 'purchasePrice':
        if (isMobile) return null;
        return (
          <TableCell>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: isTablet ? '120px' : '150px' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={0.5} sx={{ borderRadius: 5, border: '1px solid gray', px: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: '30px', textWrap: 'nowrap', fontSize: isTablet ? '0.55rem' : '0.65rem' }}>
                  MRP:
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: isTablet ? '0.55rem' : '0.65rem' }}>
                  {formatCurrency(product.mrp)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={0.5} sx={{ borderRadius: 5, border: '1px solid gray', px: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: '30px', textWrap: 'nowrap', fontSize: isTablet ? '0.55rem' : '0.65rem' }}>
                  Cost:
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: isTablet ? '0.55rem' : '0.65rem' }}>
                  <PriceCodeDisplay number={product.purchasePrice} />
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={0.5} sx={{ borderRadius: 5, border: '1px solid gray', px: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: '30px', textWrap: 'nowrap', fontSize: isTablet ? '0.55rem' : '0.65rem' }}>
                  B2C:
                </Typography>
                <Typography variant="body2" fontWeight={600} color="primary" sx={{ fontSize: isTablet ? '0.65rem' : '0.75rem' }}>
                  {formatCurrency(product.sellingPriceB2C)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center" gap={0.5} sx={{ borderRadius: 5, border: '1px solid gray', px: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ minWidth: '30px', textWrap: 'nowrap', fontSize: isTablet ? '0.55rem' : '0.65rem' }}>
                  B2B:
                </Typography>
                <Typography variant="caption" color="success.main" sx={{ fontSize: isTablet ? '0.55rem' : '0.65rem' }}>
                  {formatCurrency(product.sellingPriceB2B)}
                </Typography>
              </Box>
            </Box>
          </TableCell>
        );

      case 'compatibility':
        if (isMobile || isTablet) return null;
        return (
          <TableCell sx={{ maxWidth: 350 }}>
            {renderCompatibilityDetails(product.compatibility)}
          </TableCell>
        );

      case 'action':
        return (
          <TableCell>
            <Box display="flex" flexDirection={isMobile ? "row" : "column"} gap={0.8} flexWrap="wrap">
              <Button
                variant="text"
                onClick={() => navigate(`/inventory/${product._id}`)}
                sx={{
                  minWidth: 'auto',
                  fontSize: isMobile ? '0.5rem' : '0.60rem',
                  textWrap: 'nowrap',
                  py: 0.1,
                  px: isMobile ? 1 : 2,
                  borderRadius: 5,
                  border: '.8px solid #7f5ee666',
                  '&:hover': { backgroundColor: '#7f5ee666' }
                }}
              >
                {isMobile ? 'View' : 'View / Edit'}
              </Button>
              {canDeleteInventory() && (<Button
                size="small"
                variant="text"
                color="error"
                onClick={() => openDialog(product)}
                sx={{
                  minWidth: 'auto',
                  fontSize: isMobile ? '0.5rem' : '0.60rem',
                  py: 0.1,
                  px: isMobile ? 1 : 2,
                  borderRadius: 5,
                  border: '.8px solid #ff4d6e5e',
                  '&:hover': { backgroundColor: '#ff4d6e5e' }
                }}
              >
                Delete
              </Button>)}

            </Box>
          </TableCell>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Brandcrumb />
      </Box>
      <Card elevation={4} sx={{ overflow: 'auto' }}>
        <CardContent sx={{ pb: 1 }}>
          <Box display="flex" flexDirection={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "stretch" : "center"} flexWrap="wrap" gap={2}>
            {canCreateInventory() && (<Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/inventory/addProduct")}
              sx={{ flexShrink: 0, width: isMobile ? '100%' : 'auto' }}
              startIcon={<InventoryIcon />}
              size={isMobile ? "small" : "small"}
            >
              Add New Product
            </Button>)}

            <Box display="flex" flexDirection={isMobile ? "column" : "row"} alignItems={isMobile ? "stretch" : "center"} gap={3}>
              <Badge
                badgeContent={total}
                showZero
                color="primary"
                sx={{ alignSelf: 'center' }}
              >
                <InventoryIcon color="action" />
              </Badge>

              <TextField
                size="small"
                placeholder="Search by Part No, name..."
                variant="outlined"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  width: '100%',
                  maxWidth: isMobile ? '100%' : 300
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>
        </CardContent>

        <CardContent sx={{ pt: 1, px: isMobile ? 0.5 : 2 }}>
          <TableContainer sx={{
            maxHeight: 600,
            '& .MuiTableCell-root': {
              py: isMobile ? 0.5 : 1,
              px: isMobile ? 0.5 : 1
            }
          }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {getVisibleHeadCells().map((head) => (
                    <TableCell
                      key={head.id}
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        minWidth: head.id === 'action' && isMobile ? '80px' :
                          head.id === 'quantity' && isMobile ? '60px' : 'auto',
                        fontSize: isMobile ? '0.7rem' : '0.875rem',
                        py: isMobile ? 0.5 : 1
                      }}
                    >
                      {head.id !== 'action' ? (
                        <TableSortLabel
                          active={orderBy === head.id}
                          direction={orderBy === head.id ? order : 'asc'}
                          onClick={() => handleSort(head.id)}
                          sx={{ fontSize: 'inherit' }}
                        >
                          {head.label}
                        </TableSortLabel>
                      ) : (
                        head.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={getVisibleHeadCells().length} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={isMobile ? 20 : 24} />
                      <Typography variant="body2" sx={{ mt: 1, fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                        {debouncedSearch ? 'Searching...' : 'Loading products...'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : sortedProducts.length > 0 ? (
                  sortedProducts.map((product) => (
                    <TableRow key={product._id} hover>
                      {getVisibleHeadCells().map((head) => (
                        <React.Fragment key={head.id}>
                          {renderTableCell(product, head.id)}
                        </React.Fragment>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={getVisibleHeadCells().length} align="center" sx={{ py: 3 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <InventoryIcon sx={{ fontSize: isMobile ? 32 : 48, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                        <Typography variant="body1" color="text.secondary" gutterBottom sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>
                          {search ? `No products found for "${search}"` : 'No products available'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                          {search ? 'Try adjusting your search terms' : 'Get started by adding your first product'}
                        </Typography>
                        {!search && (
                          <Button
                            variant="contained"
                            onClick={() => navigate("/inventory/addProduct")}
                            startIcon={<InventoryIcon />}
                            sx={{ mt: 2 }}
                            size={isMobile ? "small" : "medium"}
                          >
                            Add First Product
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <DeleteConfirmDialog
        open={open}
        loading={loadingDelete}
        title="Delete Product"
        description={
          <>
            Are you sure you want to delete the product <strong>"{itemToDelete?.name}"</strong> (Part No: {itemToDelete?.partNo})? <br /><br />
            <Alert severity="warning" sx={{ fontSize: isMobile ? "10px" : "12px", py: "0px" }}>
              This action cannot be undone and all associated data will be permanently removed.
            </Alert>
          </>
        }
        onCancel={closeDialog}
        onConfirm={confirmDelete}
      />
    </>
  );
};

export default ProductsList;